/**
 * How the Comparison report writes its numbers, dates and column captions.
 *
 * Shared by the page, its Excel export and the PDF the reporting API renders, so a figure reads
 * the same wherever it is printed. They used to be the page's own methods, and the export kept
 * a second copy of the short labels; a third renderer would have made a third.
 */

export type ComparisonPeriod = 'current' | 'comparison';

export interface ComparisonDateRange {
  startDate: string;
  endDate: string;
}

export interface ComparisonRanges {
  currentRange: ComparisonDateRange;
  comparisonRange: ComparisonDateRange;
}

export type ComparisonTone = 'positive' | 'negative' | 'neutral';

const CURRENCY_FORMAT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const WHOLE_CURRENCY_FORMAT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const DECIMAL_FORMAT = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const SHORT_DECIMAL_FORMAT = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

/**
 * Dates are read and written in UTC. An ISO date is a calendar day, and reading it in the local
 * zone put it on the day before anywhere west of Greenwich once the server was not on UTC.
 */
const SHORT_MONTH_DAY = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
const SHORT_MONTH_DAY_YEAR = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});
const SHORT_MONTH_YEAR = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });

export function formatComparisonCurrency(value: number | null | undefined): string {
  return value == null ? 'N/A' : CURRENCY_FORMAT.format(value);
}

export function formatComparisonDecimal(value: number | null | undefined): string {
  return value == null ? 'N/A' : DECIMAL_FORMAT.format(value);
}

/** The figures arrive already multiplied out: 12.5 means twelve and a half percent. */
export function formatComparisonPercent(value: number | null | undefined): string {
  return value == null ? 'N/A' : `${DECIMAL_FORMAT.format(value)}%`;
}

/** A difference always carries its sign, so a gain and a loss never read alike. */
export function formatComparisonDeltaCurrency(value: number): string {
  return `${value >= 0 ? '+' : '-'}${formatComparisonCurrency(Math.abs(value))}`;
}

export function formatComparisonDeltaNumber(value: number): string {
  return `${value >= 0 ? '+' : '-'}${formatComparisonDecimal(Math.abs(value))}`;
}

/**
 * Money on a chart axis, where there is room for a few characters rather than a full figure.
 *
 * The sign goes in front of the dollar rather than after it: "-$1.2M", not "$-1.2M".
 */
export function formatComparisonCurrencyShort(value: number): string {
  const absoluteValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (absoluteValue >= 1_000_000) {
    return `${sign}$${(absoluteValue / 1_000_000).toFixed(1)}M`;
  }

  if (absoluteValue >= 1000) {
    return `${sign}$${(absoluteValue / 1000).toFixed(0)}K`;
  }

  return WHOLE_CURRENCY_FORMAT.format(value);
}

export function formatComparisonDecimalShort(value: number): string {
  return SHORT_DECIMAL_FORMAT.format(value);
}

export function getComparisonTone(value: number): ComparisonTone {
  if (value > 0) {
    return 'positive';
  }

  if (value < 0) {
    return 'negative';
  }

  return 'neutral';
}

function toUtcDate(isoDate: string): Date {
  return new Date(`${isoDate.slice(0, 10)}T00:00:00Z`);
}

/**
 * A range as briefly as it can be said: "Mar 2026" for one month, "Jan 1 - Sep 19, 2026" within a
 * year, and both years spelled out when it crosses one.
 */
export function formatComparisonCompactDateRange(startIsoDate: string, endIsoDate: string): string {
  const startDate = toUtcDate(startIsoDate);
  const endDate = toUtcDate(endIsoDate);
  const sameYear = startDate.getUTCFullYear() === endDate.getUTCFullYear();
  const sameMonth = sameYear && startDate.getUTCMonth() === endDate.getUTCMonth();

  if (sameMonth) {
    return SHORT_MONTH_YEAR.format(startDate);
  }

  if (sameYear) {
    return `${SHORT_MONTH_DAY.format(startDate)} - ${SHORT_MONTH_DAY_YEAR.format(endDate)}`;
  }

  return `${SHORT_MONTH_DAY_YEAR.format(startDate)} - ${SHORT_MONTH_DAY_YEAR.format(endDate)}`;
}

/**
 * What a column calls one side of the comparison.
 *
 * The year alone when each side sits inside a single year and the two years differ — which is
 * every year-over-year comparison, the page's default — and the range itself otherwise, since
 * "2026 Premium" over two ranges in the same year would name both columns alike.
 */
export function buildComparisonPeriodLabel(range: ComparisonDateRange, otherRange: ComparisonDateRange): string {
  const rangeStartYear = range.startDate.slice(0, 4);
  const rangeEndYear = range.endDate.slice(0, 4);
  const otherStartYear = otherRange.startDate.slice(0, 4);
  const otherEndYear = otherRange.endDate.slice(0, 4);

  if (rangeStartYear === rangeEndYear && otherStartYear === otherEndYear && rangeStartYear !== otherStartYear) {
    return rangeStartYear;
  }

  return formatComparisonCompactDateRange(range.startDate, range.endDate);
}

export function getComparisonPeriodLabel(period: ComparisonPeriod, ranges: ComparisonRanges): string {
  return period === 'current'
    ? buildComparisonPeriodLabel(ranges.currentRange, ranges.comparisonRange)
    : buildComparisonPeriodLabel(ranges.comparisonRange, ranges.currentRange);
}

/** "2026 Premium", "2025 Policies" — a figure's column named for the side it belongs to. */
export function getComparisonMetricCaption(period: ComparisonPeriod, metric: string, ranges: ComparisonRanges): string {
  return `${getComparisonPeriodLabel(period, ranges)} ${metric}`;
}

/** A no-break space after the sign, so a narrow header never leaves the "%" alone on a line. */
export function getComparisonPercentCaption(label: string): string {
  return `% ${label}`;
}

/** The whole comparison in one line: both ranges, current first. */
export function formatComparisonRangesSummary(ranges: ComparisonRanges): string {
  const current = formatComparisonCompactDateRange(ranges.currentRange.startDate, ranges.currentRange.endDate);
  const comparison = formatComparisonCompactDateRange(ranges.comparisonRange.startDate, ranges.comparisonRange.endDate);

  return `${current} vs ${comparison}`;
}
